import { useEffect, useState } from 'react';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { RefreshButton } from '../components/RefreshButton';
import { useToast } from '../components/Toast';

const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID as string;

const CONSOLE_LINKS: { label: string; description: string; path: string }[] = [
  {
    label: 'Crashlytics',
    description: 'App crashes, fatal & non-fatal errors',
    path: 'crashlytics',
  },
  {
    label: 'Performance',
    description: 'App start time, network latency, screen rendering',
    path: 'performance',
  },
  {
    label: 'Analytics',
    description: 'User engagement, retention, events',
    path: 'analytics/overview',
  },
  {
    label: 'Cloud Functions Logs',
    description: 'Backend errors and execution logs',
    path: 'functions/logs',
  },
  {
    label: 'Firestore Usage',
    description: 'Reads, writes, storage consumption',
    path: 'firestore/usage',
  },
  {
    label: 'Cloud Messaging',
    description: 'Push notification delivery reports',
    path: 'notification',
  },
];

interface HealthStat {
  label: string;
  value: string | number;
  hint?: string;
  severity?: 'ok' | 'warn' | 'error';
}

interface StuckOrder {
  id: string;
  status: string;
  updatedAt?: string;
  pickupAddress?: string;
}

export default function MonitoringPage() {
  const toast = useToast();
  const [stats, setStats] = useState<HealthStat[]>([
    { label: 'Stuck orders (>2h stale)', value: '…' },
    { label: 'Cancelled orders (24h)', value: '…' },
    { label: 'Failed payments', value: '…' },
    { label: 'Docs pending review', value: '…' },
  ]);
  const [stuckOrders, setStuckOrders] = useState<StuckOrder[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setRefreshing(true);
    (async () => {
      try {
        const twoHoursAgo = new Date(
          Date.now() - 2 * 60 * 60 * 1000
        ).toISOString();
        const dayAgo = new Date(
          Date.now() - 24 * 60 * 60 * 1000
        ).toISOString();

        // Active orders that haven't been updated in 2+ hours
        const stuckSnap = await getDocs(
          query(
            collection(db, 'orders'),
            where('status', 'in', [
              'driverAssigned',
              'enRoute',
              'pickedUp',
              'inTransit',
            ]),
            where('updatedAt', '<', twoHoursAgo),
            orderBy('updatedAt', 'asc'),
            limit(20)
          )
        );

        const [cancelled24h, failedPayments, pendingDrivers] =
          await Promise.all([
            getCountFromServer(
              query(
                collection(db, 'orders'),
                where('status', '==', 'cancelled'),
                where('updatedAt', '>=', dayAgo)
              )
            ),
            getCountFromServer(
              query(
                collection(db, 'orders'),
                where('paymentInfo.status', '==', 'failed')
              )
            ),
            getDocs(
              query(
                collection(db, 'users'),
                where('roles', 'array-contains', 'driver'),
                limit(200)
              )
            ),
          ]);

        // Count docs pending review across drivers (client-side scan)
        let pendingDocs = 0;
        pendingDrivers.docs.forEach((d) => {
          const docs = d.data().driverDocuments as
            | Record<string, { status?: string }>
            | undefined;
          if (!docs) return;
          pendingDocs += Object.values(docs).filter(
            (e) => e?.status === 'pending_review'
          ).length;
        });

        const stuckCount = stuckSnap.size;
        setStats([
          {
            label: 'Stuck orders (>2h stale)',
            value: stuckCount,
            severity: stuckCount > 0 ? 'error' : 'ok',
            hint: 'Active orders with no updates in 2+ hours',
          },
          {
            label: 'Cancelled orders (24h)',
            value: cancelled24h.data().count,
            severity: cancelled24h.data().count > 5 ? 'warn' : 'ok',
          },
          {
            label: 'Failed payments',
            value: failedPayments.data().count,
            severity: failedPayments.data().count > 0 ? 'error' : 'ok',
          },
          {
            label: 'Docs pending review',
            value: pendingDocs,
            severity: pendingDocs > 0 ? 'warn' : 'ok',
            hint: 'Driver documents awaiting approval',
          },
        ]);
        setStuckOrders(
          stuckSnap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              status: data.status,
              updatedAt: data.updatedAt,
              pickupAddress: data.pickupLocation?.address,
            };
          })
        );
        setLastUpdated(new Date());
      } catch (e) {
        console.error('Failed to load monitoring stats', e);
        toast.error('Failed to load monitoring data.');
      } finally {
        setRefreshing(false);
      }
    })();
  }, [reloadKey, toast]);

  const severityCls = (s?: HealthStat['severity']) =>
    s === 'error'
      ? 'text-red-600'
      : s === 'warn'
        ? 'text-amber-600'
        : 'text-slate-900';

  return (
    <>
      <PageHeader
        title="Monitoring"
        subtitle="Platform health, crashes, performance and backend logs."
      >
        <RefreshButton
          lastUpdated={lastUpdated}
          busy={refreshing}
          onClick={() => setReloadKey((k) => k + 1)}
        />
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {s.label}
            </div>
            <div
              className={`mt-1 text-2xl font-semibold ${severityCls(s.severity)}`}
            >
              {s.value}
            </div>
            {s.hint && (
              <div className="mt-1 text-xs text-slate-400">{s.hint}</div>
            )}
          </Card>
        ))}
      </div>

      {stuckOrders.length > 0 ? (
        <Card title="Stuck Orders">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Order</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Pickup</th>
                  <th className="px-3 py-2 text-left">Last Update</th>
                </tr>
              </thead>
              <tbody>
                {stuckOrders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{o.id}</td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        {o.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">{o.pickupAddress || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {o.updatedAt
                        ? new Date(o.updatedAt).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card title="Stuck Orders">
          <p className="text-sm text-slate-500">
            No stuck orders — all active deliveries have been updated within
            the last 2 hours.
          </p>
        </Card>
      )}

      <Card title="Firebase Console">
        <p className="mb-4 text-xs text-slate-500">
          Crash reports, performance traces and detailed logs live in the
          Firebase Console. These links open the relevant section for this
          project.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CONSOLE_LINKS.map(({ label, description, path }) => (
            <a
              key={path}
              href={`https://console.firebase.google.com/project/${PROJECT_ID}/${path}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-slate-200 p-4 transition hover:border-brand-400 hover:bg-slate-50"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">{label}</span>
                <span className="text-slate-400">↗</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">{description}</div>
            </a>
          ))}
        </div>
      </Card>
    </>
  );
}
